import { DsAvatar, DsImage } from '@ice-web-app/designSystem';
import { useCurrentUserStore, useGlobalStore } from '@ice-web-app/shared-store';
import { Box, Divider, Fade, Stack } from '@mui/material';
import { styled, useColorScheme, useTheme } from '@mui/material/styles';
import { useEffect, useRef } from 'react';
import MICRO_LOGO from '../../../../../../assets/images/micro-logo.png';
import { DsIcon } from '../../../../components/icons/DsIcon';
import { useSettings } from '../../../@core/hooks/useSettings';
import navigationCustomStyles from '../../../@core/styles/vertical/navigationCustomStyles';
import type { Mode, SystemMode } from '../../../@core/types';
import useVerticalNav from '../../../@menu/hooks/useVerticalNav';
import VerticalNav, { NavCollapseIcons, NavHeader } from '../../../@menu/vertical-menu';
import Logo from '../shared/Logo';
import { VerticalMenu } from './VerticalMenu';
import MiniNavbar from './miniNavbar/MiniNavbar';

type Props = {
  mode: Mode;
  systemMode: SystemMode;
  miniSidebar: boolean;
  sidebarHeader: boolean;
};

const _StyledBoxForShadow = styled('div')(({ theme }) => ({
  top: 60,
  left: -8,
  zIndex: 2,
  opacity: 0,
  position: 'absolute',
  pointerEvents: 'none',
  width: 'calc(100% + 15px)',
  height: theme.mixins.toolbar.minHeight,
  transition: 'opacity .15s ease-in-out',
  background: `linear-gradient(var(--mui-palette-background-default) ${
    theme.direction === 'rtl' ? '95%' : '5%'
  }, rgb(var(--mui-palette-background-defaultChannel) / 0.85) 30%, rgb(var(--mui-palette-background-defaultChannel) / 0.5) 65%, rgb(var(--mui-palette-background-defaultChannel) / 0.3) 75%, transparent)`,
  '&.scrolled': {
    opacity: 1,
  },
}));

export const VerticalNavigation = (props: Props) => {
  // Props
  const { mode, systemMode, miniSidebar, sidebarHeader } = props;

  // Hooks
  const verticalNavOptions = useVerticalNav();
  const { updateSettings, settings } = useSettings();
  const { mode: muiMode, systemMode: muiSystemMode } = useColorScheme();
  const theme = useTheme();

  const { showNavCollapseIcon, showMiniSidebar, showMainSidebar } = useGlobalStore();
  const { currentMicroSystem } = useCurrentUserStore();
  // Refs
  const shadowRef = useRef(null);

  // Vars
  const { isCollapsed, isHovered, collapseVerticalNav, isBreakpointReached } = verticalNavOptions;
  const isServer = typeof window === 'undefined';
  const isSemiDark = settings.semiDark;
  let isDark: boolean;

  if (isServer) {
    isDark = mode === 'system' ? systemMode === 'dark' : mode === 'dark';
  } else {
    isDark = muiMode === 'system' ? muiSystemMode === 'dark' : muiMode === 'dark';
  }

  const scrollMenu = (container: any, isPerfectScrollbar: boolean) => {
    const containerVar = isBreakpointReached || !isPerfectScrollbar ? container.target : container;

    if (shadowRef && containerVar.scrollTop > 0) {
      if (!(shadowRef.current as any).classList.contains('scrolled')) {
        (shadowRef.current as any).classList.add('scrolled');
      }
    } else {
      (shadowRef.current as any).classList.remove('scrolled');
    }
  };

  useEffect(() => {
    if (settings.layout === 'collapsed') {
      collapseVerticalNav(true);
    } else {
      collapseVerticalNav(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.layout]);

  return !(miniSidebar && showMiniSidebar) && !showMainSidebar ? null : (
    // eslint-disable-next-line lines-around-comment
    // Sidebar Vertical Menu
    <VerticalNav
      customStyles={navigationCustomStyles(verticalNavOptions, theme)}
      collapsedWidth={miniSidebar && showMiniSidebar ? 135 : 68}
      backgroundColor="var(--mui-palette-background-paper)"
      // eslint-disable-next-line lines-around-comment
      // The following condition adds the data-mui-color-scheme='dark' attribute to the VerticalNav component
      // when semiDark is enabled and the mode or systemMode is light
      {...(isSemiDark &&
        !isDark && {
          'data-mui-color-scheme': 'dark',
        })}
    >
      {/* Nav Header including Logo & nav toggle icons  */}
      {sidebarHeader && (
        <NavHeader>
          <Stack direction="row" alignItems="center" gap={8}>
            <a href="/">
              <Logo />
            </a>
            <Fade in={!(isCollapsed && !isHovered)}>
              <span>
                {/* <DsAvatar
                imgUrl={currentMicroSystem.logo}
                alt={currentMicroSystem.name}
                sx={{ width: 150, height: 60, borderRadius: 0 }}
              /> */}
                <DsImage
                  imgUrl={currentMicroSystem?.logo}
                  alt={currentMicroSystem?.name}
                  width={150}
                  height={60} /* sx={{ width: 150, height: 60 }} */
                />
              </span>
            </Fade>
          </Stack>
          {!(isCollapsed && !isHovered) && showNavCollapseIcon && (
            <NavCollapseIcons
              lockedIcon={<DsIcon icon="CHEVRON_LEFT" size="md" color="iceGray.600" />}
              unlockedIcon={<DsIcon icon="CHEVRON_RIGHT" size="md" color="iceGray.600" />}
              closeIcon={<DsIcon icon="CLOSE" />}
              onClick={() =>
                updateSettings({
                  layout: !isCollapsed ? 'collapsed' : 'vertical',
                })
              }
            />
          )}
        </NavHeader>
      )}
      {/* <StyledBoxForShadow ref={shadowRef} /> */}
      <Stack
        sx={{
          height: '100%',
          '& .scrollbar-container': {
            width: '100%',
          },
          '& .bs-full': {
            width: '100%',
          },
          position: 'relative',
          blockSize: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
        direction="row"
        divider={<Divider orientation="vertical" flexItem />}
      >
        {miniSidebar && showMiniSidebar && <MiniNavbar />}
        {showMainSidebar &&
          (!isCollapsed || isHovered ? (
            <Stack sx={{ width: '100%', height: 1 }}>
              <VerticalMenu scrollMenu={scrollMenu} />
            </Stack>
          ) : (
            <VerticalMenu scrollMenu={scrollMenu} />
          ))}
      </Stack>
    </VerticalNav>
  );
};
